import { createSelector } from "@reduxjs/toolkit";
import ReduxState from "../models";
import { getWorkspace, getWorkspaceBillingPrice, getWorkspaceContents, getPipelineCenter } from "./workspace.selectors";
import checkSubscription from "../../utils/subscription-limits/check-subscription";
import { getFilteredDefaultPipelines, isFormBooked } from "../../components/navigation/routes-generation-helper";
import {
    generateCustomPipelinesInfo,
    generateVisibleCustomPipeline,
    getFullAccessedPipelines,
    generateVisibleDefaultPipeline,
    defaultPipelinesInfo,
    prospectiveBuyerPipeline,
    networkPartnerPipeline
} from "../../components/pipeline-center/pipeline-center-helpers";

export const getPipelineObj = (state: ReduxState) => state.pipelines;
export const getPipelines = createSelector(getPipelineObj, p => p.pipelines);
export const getPipelineSelectList = createSelector(getPipelineObj, p => p.selectList); // next pipelines
export const getAccessedPipelines = createSelector(getPipelineObj, p => p.accessed);
export const getPipelineNames = createSelector(getPipelineObj, p => p.names);

export const isContentBooked = createSelector(getWorkspaceContents, contents => {
    const guides = contents?.guides;
    const checklists = contents?.checklists;

    return (
        (guides && Object.values(guides)?.some((guide: any) => guide?.booked)) ||
        (checklists && Object.values(checklists)?.some((checklist: any) => checklist?.booked))
    );
});

export const getVisiblePipelines = createSelector(getWorkspaceBillingPrice, getWorkspace, isContentBooked, (price, workspace, booked) => {
    const accessSalesPipelineSub = checkSubscription(price, "accessSalesPipeline");
    const isPartner = workspace?.integrations?.leadConnect?.vrBank?.isPartner;
    const filteredDefaultPipelines = getFilteredDefaultPipelines(workspace);

    const defaultBookedPipelines = booked ? filteredDefaultPipelines : filteredDefaultPipelines.filter(item => item.pipeline !== "marketing-content");
    const actualDefaultPipelines = isPartner ? defaultBookedPipelines : defaultBookedPipelines?.filter(item => item.pipeline !== "vr-bank-partners");
    return actualDefaultPipelines?.filter((p: any) => (p.noAccessForNewAccounts ? accessSalesPipelineSub : true)) ?? actualDefaultPipelines;
});

export const getVisibleDefaultRealEstatePipelines = createSelector(getVisiblePipelines, getPipelineCenter, (visiblePipelines, pipelineCenter) => {
    const defaultVisible = visiblePipelines
        ?.map(pipeline => generateVisibleDefaultPipeline({ info: pipeline, pipelineCenter, relatedTo: "realEstate" }))
        ?.filter(pipeline => pipeline !== undefined);

    return defaultVisible?.filter(p => p.relatedTo === "realEstate");
});

export const getCustomPipelinesList = createSelector(getPipelines, customPipelines => {
    if (customPipelines?.length > 0) {
        const list = generateCustomPipelinesInfo(customPipelines);
        return list;
    }
    return null;
});

export const getVisibleAccessedRealEstatePipelines = createSelector(getAccessedPipelines, getPipelines, (accessed, customPipelines) => {
    if (accessed && customPipelines?.length > 0) {
        const fullAccessed = getFullAccessedPipelines(accessed, customPipelines);
        const customVisible = fullAccessed
            ?.map(pipeline => generateVisibleCustomPipeline({ info: pipeline, relatedTo: "realEstate" }))
            ?.filter(pipeline => pipeline !== undefined)
            ?.filter(p => p.relatedTo === "realEstate");

        return customVisible;
    }
    return [];
});

export const getVisibleAccessedContactPipelines = createSelector(
    getAccessedPipelines,
    getPipelines,
    (_s, variant: string) => variant,
    (accessed, customPipelines, variant) => {
        const contactPipelines = customPipelines.filter(p => p.relatedTo === "contact");
        if (accessed && contactPipelines?.length > 0) {
            const fullAccessed = getFullAccessedPipelines(accessed, contactPipelines) ?? [];

            const visiblePipelines = fullAccessed
                ?.map(pipeline => generateVisibleCustomPipeline({ info: pipeline }))
                ?.filter(pipeline => pipeline !== undefined);

            if (variant === "prospectiveBuyer") {
                visiblePipelines.push(prospectiveBuyerPipeline);
            } else if (variant === "networkPartner") {
                visiblePipelines.push(networkPartnerPipeline);
            }

            const list = generateCustomPipelinesInfo(contactPipelines);
            const currentPipelinesList = {
                ...list,
                [prospectiveBuyerPipeline.pipeline]: prospectiveBuyerPipeline,
                [networkPartnerPipeline.pipeline]: networkPartnerPipeline
            };

            return { visiblePipelines, currentPipelinesList };
        }
        return { visiblePipelines: [], currentPipelinesList: null };
    }
);

export const getFullContactPipelines = currentPipelines => {
    return currentPipelines?.reduce((acc, p) => {
        if (p?.subcategories?.length > 0) {
            p?.subcategories?.map(sub => (acc[sub._id] = sub));
        } else {
            acc[p._id] = p;
        }
        return acc;
    }, {});
};

export const getAllPipelines = createSelector(getCustomPipelinesList, currentPipelinesList => {
    return { ...currentPipelinesList, ...defaultPipelinesInfo };
});
